import { Alert, Button, EmptyState, LoadingState, useToast } from '@orbind/ui'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { PERMISSAO_DO_CONECTOR_ROTULO, type PermissaoDoConector } from '@orbind/core'
import { api } from '../lib/api.ts'
import { useSession } from '../lib/session.tsx'
import './conectar-ao-claude.css'

/**
 * Consentimento OAuth (SPEC-20261103-1822): o claude.ai (ou outro cliente
 * MCP) mandou a pessoa para cá autorizar. A URL só traz o id do pedido, então
 * sobrevive ao login. A pessoa escolhe o workspace (só os em que foi liberada)
 * e autoriza ou recusa; nos dois casos o navegador volta para o cliente.
 */

type Pedido = {
  cliente: { name: string; uri: string | null }
  destino: string
  expiraEm: string
  workspaces: { id: string; name: string; atual: boolean; liberado: boolean; permissoes: PermissaoDoConector[] }[]
}

export function ConsentimentoDoClaudePage() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const toast = useToast()
  const sessao = useSession()

  const pedido = useQuery({
    queryKey: ['ai-connectors', 'oauth', id],
    queryFn: async () => {
      const r = await api['ai-connectors'].oauth.grants[':id'].$get({ param: { id } })
      const corpo = (await r.json()) as Pedido & { error?: { message?: string } }
      if (!r.ok) throw new Error(corpo.error?.message ?? 'Pedido inválido.')
      return corpo
    },
    retry: false,
  })

  const [workspaceId, setWorkspaceId] = useState<string | null>(null)
  useEffect(() => {
    if (!pedido.data || workspaceId) return
    const liberados = pedido.data.workspaces.filter((w) => w.liberado)
    setWorkspaceId(liberados.find((w) => w.atual)?.id ?? liberados[0]?.id ?? null)
  }, [pedido.data, workspaceId])

  const responder = useMutation({
    mutationFn: async (autoriza: boolean) => {
      const r = autoriza
        ? await api['ai-connectors'].oauth.grants[':id'].consent.$post({ param: { id }, json: { workspaceId: workspaceId! } })
        : await api['ai-connectors'].oauth.grants[':id'].deny.$post({ param: { id } })
      const corpo = (await r.json()) as { redirect?: string; error?: { message?: string } }
      if (!r.ok || !corpo.redirect) throw new Error(corpo.error?.message ?? 'Não foi possível responder ao pedido.')
      return corpo.redirect
    },
    onSuccess: (destino) => window.location.assign(destino),
    onError: (e) => toast.push({ tone: 'danger', message: e instanceof Error ? e.message : 'Não foi possível responder.' }),
  })

  if (pedido.isPending) {
    return (
      <div className="cac">
        <LoadingState variant="detail" label="Carregando o pedido" />
      </div>
    )
  }
  if (pedido.isError) {
    return (
      <div className="cac">
        <div className="cac__inner">
          <Alert tone="danger" title="Este pedido de autorização não vale mais">
            {pedido.error instanceof Error ? pedido.error.message : 'Volte ao Claude e tente conectar de novo.'}
          </Alert>
          <div>
            <Button variant="secondary" icon="fas fa-robot" onClick={() => navigate('/claude')}>
              Ir para Conectar ao Claude
            </Button>
          </div>
        </div>
      </div>
    )
  }

  const p = pedido.data
  const liberados = p.workspaces.filter((w) => w.liberado)
  const escolhido = p.workspaces.find((w) => w.id === workspaceId) ?? null

  return (
    <div className="cac">
      <div className="cac__inner cac__inner--estreito">
        <header className="cac__head">
          <h1 className="cac__title">
            <i className="fas fa-robot" aria-hidden /> Autorizar o acesso ao Orbind
          </h1>
          <p className="cac__sub">
            <strong>{p.cliente.name}</strong>
            {p.cliente.uri ? <> ({p.cliente.uri})</> : null} quer usar o Orbind em seu nome, como{' '}
            <strong>{sessao.name}</strong> ({sessao.email}). Depois de responder, o navegador volta para{' '}
            <code className="cac__inline">{p.destino}</code>.
          </p>
        </header>

        {liberados.length === 0 ? (
          <EmptyState
            icon="fas fa-user-lock"
            title="Você ainda não foi liberado em nenhum workspace"
            description="Quem administra o workspace libera pessoas na tela Conectores de IA, na Administração. Depois, volte ao Claude e tente de novo."
            action={
              <Button variant="secondary" onClick={() => responder.mutate(false)} disabled={responder.isPending}>
                Voltar ao Claude sem autorizar
              </Button>
            }
          />
        ) : (
          <>
            <section className="cac__passo">
              <h2 className="cac__h2">Em qual workspace?</h2>
              <ul className="cac__workspaces" aria-label="Workspaces">
                {p.workspaces.map((w) => (
                  <li key={w.id}>
                    <button
                      type="button"
                      className={`cac__workspace${w.id === workspaceId ? ' cac__workspace--escolhido' : ''}`}
                      disabled={!w.liberado}
                      aria-pressed={w.id === workspaceId}
                      onClick={() => setWorkspaceId(w.id)}
                    >
                      <i className={w.id === workspaceId ? 'fas fa-check-circle' : 'far fa-circle'} aria-hidden />
                      <span className="cac__workspace-nome">{w.name}</span>
                      <span className="cac__workspace-meta">
                        {w.liberado ? `${w.permissoes.length} permissões` : 'não liberado pelo administrador'}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </section>

            {escolhido ? (
              <section className="cac__passo">
                <h2 className="cac__h2">O que o Claude poderá fazer</h2>
                <ul className="cac__permissoes">
                  {escolhido.permissoes.map((perm) => (
                    <li key={perm}>
                      <i className="fas fa-check" aria-hidden /> {PERMISSAO_DO_CONECTOR_ROTULO[perm]}
                    </li>
                  ))}
                </ul>
                <p className="cac__texto">
                  Sempre com a sua visibilidade e o seu perfil; toda escrita pede sua confirmação na conversa, e tudo
                  fica no log da tela Conectar ao Claude. Você pode revogar a qualquer hora.
                </p>
              </section>
            ) : null}

            <div className="cac__novo-acoes">
              <Button
                variant="primary"
                icon="fas fa-check"
                disabled={!escolhido || responder.isPending}
                onClick={() => responder.mutate(true)}
              >
                Autorizar
              </Button>
              <Button variant="secondary" icon="fas fa-times" disabled={responder.isPending} onClick={() => responder.mutate(false)}>
                Recusar
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
